import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EventTypeChannel, SkipReason, TaskNextStep, TaskPriority, TaskType } from '@brimo/shared';
import { api } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import type { TaskItem, UserRef } from '../tasks/api';

/** Types of the queue routes (docs/dev/api-queue.md). */

export type QueueBlock = 'overdue' | 'fresh_leads' | 'appointments_without_outcome' | 'documents';
export type QueueOrder = 'score' | 'due' | 'newest_first';
export type QueueDialMode = 'preview' | 'power';

export interface QueueFilter {
  types?: TaskType[];
  priorities?: TaskPriority[];
  dueFrom?: string;
  dueTo?: string;
  lifecycle?: string[];
  propertyId?: string;
  contactId?: string;
  /** Explicit tasks (the skipped ones of a summary — „Beide jetzt bearbeiten"). */
  taskIds?: string[];
}

export interface SuggestionsResponse {
  open: { total: number; overdue: number };
  blocks: Array<{ key: QueueBlock; label: string; count: number; hint: string | null }>;
  orders: Array<{ key: QueueOrder; label: string; hint: string | null }>;
  dialModes: Array<{ key: QueueDialMode; label: string; hint: string | null }>;
  minutesPerEntry: number;
  activeSessionId: string | null;
}

export interface PreviewResponse {
  count: number;
  minutes: number;
  hint: string | null;
}

export interface QueueSession {
  id: string;
  status: 'running' | 'paused' | 'ended';
  position: number;
  total: number;
  blocks: QueueBlock[];
  filter: QueueFilter | null;
  order: QueueOrder;
  dialMode: QueueDialMode;
  sourceLabel: string;
  startedAt: string;
  pausedAt: string | null;
  endedAt: string | null;
}

export interface QueueContactCard {
  id: string;
  name: string;
  initials: string;
  roleLine: string;
  lifecycle: string;
  dnc: boolean;
  phone: string | null;
  email: string | null;
  advisor: UserRef | null;
  deal: { id: string; label: string; stage: string; number: number } | null;
  lastContact: string | null;
  noShowCount: number;
}

export interface QueueAppointment {
  id: string;
  eventTypeName: string;
  startsAt: string;
  endsAt: string;
  channel: EventTypeChannel;
  status: 'booked' | 'rescheduled' | 'cancelled' | 'attended' | 'no_show';
  outcome: 'attended' | 'no_show' | 'rescheduled' | null;
  advisor: UserRef;
  label: string;
  wrapUpTaskId: string | null;
  href: string;
}

export interface QueueEntry {
  id: string;
  position: number;
  kind: 'task' | 'appointment' | 'contact';
  status: 'open' | 'done' | 'skipped' | 'later' | 'aborted';
  task: TaskItem | null;
  appointment: QueueAppointment | null;
  contact: QueueContactCard | null;
}

export interface QueueState {
  session: QueueSession | null;
  entry: QueueEntry | null;
  progress: { position: number; total: number; percent: number };
  counts: { done: number; skipped: number; later: number };
}

export interface AdvanceBody {
  result: 'done' | 'skipped' | 'later';
  taskCompletion?: { outcomeType?: string | null; outcomeText?: string | null; nextStep?: TaskNextStep };
  skip?: { reason: SkipReason; note?: string | null };
  later?: { dueOn: string; dueTime?: string | null };
}

export interface AdvanceResponse extends QueueState {
  next: 'book_appointment' | null;
  createdTaskId: string | null;
  skip: { skipCount: number; stuck: boolean } | null;
}

export interface OpenItem {
  entryId: string;
  kind: 'task' | 'appointment' | 'contact';
  taskId: string | null;
  appointmentId: string | null;
  title: string;
  statusText: string;
  status: 'skipped' | 'later';
  statusLabel: 'übersprungen' | 'später';
  href: string;
}

export interface SessionSummary {
  sessionId: string;
  endReason: 'manual' | 'timeout_24h';
  durationSeconds: number;
  durationLabel: string;
  total: number;
  done: number;
  skipped: number;
  later: number;
  appointmentsBooked: number;
  openItems: OpenItem[];
  allDone: boolean;
  nextDue: { on: string; time: string | null; label: string } | null;
}

export interface StartSessionBody {
  blocks?: QueueBlock[];
  filter?: QueueFilter | null;
  order?: QueueOrder;
  dialMode?: QueueDialMode;
}

export const currentQueueKey = [...queryRoots.queue, 'current'] as const;
export const suggestionsKey = [...queryRoots.queue, 'suggestions'] as const;
export const summaryKey = (id: string) => [...queryRoots.queue, 'summary', id] as const;
export const previewKey = (body: { blocks: QueueBlock[]; filter: QueueFilter | null }) =>
  [...queryRoots.queue, 'preview', body] as const;

/** The running or paused session of the user (`session: null` without one); realtime `queue.state` refreshes it. */
export function useCurrentQueue(enabled = true) {
  return useQuery({
    queryKey: currentQueueKey,
    queryFn: () => api<QueueState>('/queue/sessions/current'),
    enabled,
    staleTime: 10_000,
  });
}

export function useQueueSuggestions() {
  return useQuery({
    queryKey: suggestionsKey,
    queryFn: () => api<SuggestionsResponse>('/queue/suggestions'),
  });
}

export function useQueuePreview(
  body: { blocks: QueueBlock[]; filter: QueueFilter | null },
  enabled: boolean,
) {
  return useQuery({
    queryKey: previewKey(body),
    queryFn: () => api<PreviewResponse>('/queue/preview', { method: 'POST', body }),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useQueueSummary(id: string | null) {
  return useQuery({
    queryKey: summaryKey(id ?? ''),
    queryFn: () => api<SessionSummary>(`/queue/sessions/${id ?? ''}/summary`),
    enabled: id !== null,
    staleTime: Infinity,
  });
}

function useInvalidateQueue() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryRoots.queue }),
      queryClient.invalidateQueries({ queryKey: queryRoots.tasks }),
      queryClient.invalidateQueries({ queryKey: queryRoots.session }),
    ]);
  };
}

export function useStartQueue() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: (body: StartSessionBody) => api<QueueState>('/queue/sessions', { method: 'POST', body }),
    onSuccess: async (state) => {
      queryClient.setQueryData(currentQueueKey, state);
      await invalidate();
    },
  });
}

export function useAdvanceQueue() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdvanceBody }) =>
      api<AdvanceResponse>(`/queue/sessions/${id}/advance`, { method: 'POST', body }),
    onSuccess: async (state) => {
      queryClient.setQueryData(currentQueueKey, {
        session: state.session,
        entry: state.entry,
        progress: state.progress,
        counts: state.counts,
      } satisfies QueueState);
      await invalidate();
    },
  });
}

export function usePauseQueue() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'pause' | 'resume' }) =>
      api<QueueState>(`/queue/sessions/${id}/${action}`, { method: 'POST', body: {} }),
    onSuccess: async (state) => {
      queryClient.setQueryData(currentQueueKey, state);
      await invalidate();
    },
  });
}

export function useEndQueue() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: (id: string) =>
      api<SessionSummary>(`/queue/sessions/${id}/end`, { method: 'POST', body: {} }),
    onSuccess: async (summary) => {
      queryClient.setQueryData(summaryKey(summary.sessionId), summary);
      queryClient.setQueryData(currentQueueKey, {
        session: null,
        entry: null,
        progress: { position: 0, total: 0, percent: 0 },
        counts: { done: 0, skipped: 0, later: 0 },
      } satisfies QueueState);
      await invalidate();
    },
  });
}

/** Page title of a task entry in focus mode: „Stefan Brunnthaler anrufen", „Termin nachbereiten: <Name>", else the task title. */
export function entryTitle(entry: QueueEntry): string {
  if (entry.kind === 'appointment' && entry.appointment) {
    const name = entry.contact?.name;
    return name ? `Termin nachbereiten: ${name}` : entry.appointment.eventTypeName;
  }
  const task = entry.task;
  if (!task) return entry.contact?.name ?? '';
  const name = task.contact?.name;
  if (name && (task.type === 'call' || task.type === 'follow_up')) return `${name} anrufen`;
  if (name && task.type === 'appointment_outcome') return `Termin nachbereiten: ${name}`;
  return task.title;
}
